import { useCallback, useEffect, useRef, useState } from "react";
import { IModules } from "../../../../models/IUser";
import {
  DeviceFieldKey,
  FIELD_VALIDATORS,
} from "../deviceTypeConfig";

export function useModuleFieldState(
  module: IModules | null,
  markMutated: () => void,
) {
  const [editedModule, setEditedModule] = useState<IModules | null>(null);
  const [changedModuleFields, setChangedModuleFields] = useState<Set<string>>(
    new Set(),
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // `АУД-Н329` (возврат): набор изменённых полей читается эффектом через ref,
  // а сам эффект зависит только от `[module]`, как в `dev`. Иначе `reset()`
  // после «Сохранить» перезапускал эффект и собирал черновик из старого
  // `activeModule`.
  const changedModuleFieldsRef = useRef(changedModuleFields);

  useEffect(() => {
    changedModuleFieldsRef.current = changedModuleFields;
  }, [changedModuleFields]);

  useEffect(() => {
    if (!module) {
      setEditedModule(null);
      return;
    }

    // `ЗАМ.3` (`АУД-Н323`): обновление `module` с сервера не должно затирать
    // несохранённые поля окна. Новые значения берём с сервера, изменённые —
    // оставляем из черновика.
    setEditedModule((prev) => {
      if (!prev) return { ...module };

      const next = { ...module } as IModules;

      for (const key of changedModuleFieldsRef.current) {
        // `ТИП-Q1`: запись через `keyof IModules` даёт TS2322 (`never`).
        Object.assign(next, { [key]: prev[key as keyof IModules] });
      }

      return next;
    });
  }, [module]);

  const validateValue = useCallback(
    (name: string, value: unknown): string | null => {
      const validators = FIELD_VALIDATORS[name as DeviceFieldKey];
      if (!validators) return null;
      const strValue = value == null ? "" : String(value);
      for (const validate of validators) {
        const error = validate(strValue);
        if (error) return error;
      }
      return null;
    },
    [],
  );

  const applyValidation = useCallback(
    (name: string, value: unknown) => {
      const error = validateValue(name, value);
      setFieldErrors((prev) => {
        if (error) return { ...prev, [name]: error };
        if (!(name in prev)) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      });
    },
    [validateValue],
  );

  const trackField = (name: string) => {
    setChangedModuleFields((prev) => {
      const next = new Set(prev);
      next.add(name);
      return next;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    markMutated();
    const { name, value } = e.target;

    setEditedModule((prev) => (prev ? { ...prev, [name]: value } : prev));
    trackField(name);
    applyValidation(name, value);
  };

  const setField = (name: keyof IModules, value: unknown) => {
    markMutated();
    setEditedModule((prev) => (prev ? { ...prev, [name]: value } : prev));
    trackField(name as string);
    applyValidation(name as string, value);
  };

  const validateAll = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!editedModule) return errors;

    for (const name of changedModuleFields) {
      const error = validateValue(name, editedModule[name as keyof IModules]);
      if (error) {
        errors[name] = error;
      }
    }

    setFieldErrors(errors);
    return errors;
  }, [editedModule, changedModuleFields, validateValue]);

  const reset = () => {
    setChangedModuleFields(new Set());
    setFieldErrors({});
  };

  /**
   * `ЗАМ.3`: поле уже сохранено на сервере — обновляем черновик, но в
   * `changedModuleFields` его не оставляем: следующее «Сохранить» его не шлёт.
   */
  const applySavedField = useCallback((name: keyof IModules, value: unknown) => {
    setEditedModule((prev) => (prev ? { ...prev, [name]: value } : prev));
    setChangedModuleFields((prev) => {
      const next = new Set(prev);
      next.delete(name as string);
      return next;
    });
  }, []);

  return {
    editedModule,
    changedModuleFields,
    changedModuleFieldKeys: Array.from(changedModuleFields),
    fieldErrors,
    handleChange,
    setField,
    applySavedField,
    validateAll,
    reset,
  };
}
